import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import {
  Typography,
  Box,
  Avatar,
  IconButton,
  Switch,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { PowerSettingsNew as LogoutIcon } from '@mui/icons-material'
import AuthContext from '../contexts/AuthContext'
import ThemeContext from '../contexts/ThemeContext'

// Advanced Material UI Switch styled component (iOS style) with responsive support
const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
    width: 32,
    height: 32,
    '&:before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#fff'
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.861A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.861 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
    borderRadius: 20 / 2,
  },
  // Responsive styles
  [theme.breakpoints.down('sm')]: {
    width: 54,
    height: 30,
    padding: 6,
    '& .MuiSwitch-thumb': {
      width: 28,
      height: 28,
    },
    '& .MuiSwitch-switchBase': {
      transform: 'translateX(4px)',
      '&.Mui-checked': {
        transform: 'translateX(20px)',
      },
    },
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: 58,
    height: 32,
    padding: 6.5,
    '& .MuiSwitch-thumb': {
      width: 30,
      height: 30,
    },
    '& .MuiSwitch-switchBase': {
      transform: 'translateX(5px)',
      '&.Mui-checked': {
        transform: 'translateX(21px)',
      },
    },
  },
}))

// Styled Racepack Link
const StyledRacepackLink = styled(RouterLink)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.3s ease',
}))

const Navbar = ({ onMenuClick = null }) => {
  const { user, removeToken } = useContext(AuthContext)
  const { theme, toggleTheme } = useContext(ThemeContext)
  const [anchorEl, setAnchorEl] = useState(null)
  const profileMenuOpen = Boolean(anchorEl)

  // Get first letter of user name for avatar
  const getInitials = (name) => {
    if (!name) return 'A'
    return name.charAt(0).toUpperCase()
  }

  // Handle profile menu
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleProfileMenuClose()
    removeToken()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 1.5, md: 2 },
        minHeight: { xs: 56, sm: 64, md: 64 },
      }}>
      {/* Left Section: Menu Icon + Racepack */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          flex: '0 0 auto',
          minWidth: 0, // Allow shrinking
        }}>
        {/* Menu Icon Button for Mobile */}
        {onMenuClick && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{
              display: { md: 'none' },
              color: 'text.primary',
              padding: { xs: 0.75, sm: 1 },
              marginRight: { xs: 0.5, sm: 0.75 },
            }}>
            <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '1.75rem' } }} />
          </IconButton>
        )}

        <StyledRacepackLink to="/">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.625rem' },
              letterSpacing: { xs: '0.25px', sm: '0.5px', md: '0.75px' },
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                    : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                transform: 'scale(1.05)',
              },
            }}>
            Racepack
          </Typography>
        </StyledRacepackLink>
      </Box>

      {/* Right Section: Switch + User Profile */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1.25, md: 1.5 },
          flex: '0 0 auto',
          minWidth: 0, // Allow shrinking
        }}>
        {/* Dark/Light Mode Switch */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <MaterialUISwitch
            checked={theme === 'dark'}
            onChange={toggleTheme}
            inputProps={{ 'aria-label': 'toggle dark mode' }}
          />
        </Box>

        {/* User Profile Section with Dropdown */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1, md: 1.25 },
            marginLeft: { xs: 0.5, sm: 0.75, md: 1 },
            cursor: 'pointer',
          }}
          onClick={handleProfileClick}>
          <Avatar
            sx={{
              width: { xs: 32, sm: 36, md: 40 },
              height: { xs: 32, sm: 36, md: 40 },
              bgcolor: (theme) =>
                theme.palette.mode === 'light' ? 'rgba(224, 224, 224, 1)' : 'rgba(66, 66, 66, 1)',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'light'
                    ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                    : '0 2px 8px rgba(255, 255, 255, 0.1)',
              },
            }}>
            {getInitials(user?.name)}
          </Avatar>

          {/* User Info - Hidden on mobile */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              minWidth: 0, // Allow text truncation
            }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { sm: '0.875rem', md: '0.9375rem' },
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { sm: '120px', md: '150px' },
              }}>
              {user?.name || 'Admin1'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { sm: '0.75rem', md: '0.8125rem' },
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { sm: '120px', md: '150px' },
              }}>
              {user?.email || 'Admin@123'}
            </Typography>
          </Box>
        </Box>

        {/* Profile Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={profileMenuOpen}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: { xs: 0.75, sm: 1, md: 1 },
              minWidth: { xs: 180, sm: 200, md: 220 },
              borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiAvatar-root': {
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                ml: { xs: 1, sm: 1.25, md: 1.5 },
                mr: { xs: 1, sm: 1.25, md: 1.5 },
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: { xs: 12, sm: 16, md: 20 },
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                borderLeft: '1px solid',
                borderTop: '1px solid',
                borderColor: 'divider',
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
          {/* User Info Section */}
          <MenuItem
            disabled
            sx={{
              opacity: 1,
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 1, sm: 1.25, md: 1.5 },
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}>
            <Avatar
              sx={{
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                bgcolor: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(224, 224, 224, 1)' : 'rgba(66, 66, 66, 1)',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
                border: '1px solid',
                borderColor: 'divider',
              }}>
              {getInitials(user?.name)}
            </Avatar>
            <Box
              sx={{
                ml: { xs: 1, sm: 1.25, md: 1.5 },
                minWidth: 0,
                flex: 1,
              }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {user?.name || 'Admin1'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: 0.25,
                }}>
                {user?.email || 'Admin@123'}
              </Typography>
            </Box>
          </MenuItem>

          <Divider sx={{ my: { xs: 0.5, sm: 0.625, md: 0.75 } }} />

          {/* Logout Button */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 1, sm: 1.25, md: 1.5 },
              '&:hover': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(244, 67, 54, 0.08)'
                    : 'rgba(244, 67, 54, 0.15)',
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: { xs: 32, sm: 36, md: 40 },
                color: (theme) => (theme.palette.mode === 'light' ? '#f44336' : '#ef5350'),
              }}>
              <LogoutIcon
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              secondary="Logout your Account"
              primaryTypographyProps={{
                sx: {
                  fontWeight: 600,
                  color: (theme) => (theme.palette.mode === 'light' ? '#f44336' : '#ef5350'),
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                },
              }}
              secondaryTypographyProps={{
                sx: {
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
                  color: 'text.secondary',
                },
              }}
            />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )
}

Navbar.propTypes = {
  onMenuClick: PropTypes.func,
}

export default Navbar
